import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import TokenPositionCard from './TokenPositionCard';
import { usePortfolioWebSocket } from '../../hooks/usePortfolioWebSocket';

const MyTokensTab = ({
  onQuickSell,
  onAddToBot,
  onSetAlert,
  portfolioSummary = {},
  refreshTrigger = 0
}) => {
  const { tokens } = useAuth();
  const { activeWallet } = useWallet();
  const [myTokens, setMyTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // WebSocket integration for real-time portfolio updates
  const {
    portfolioData: wsPortfolioData,
    isConnected: wsConnected,
    isSubscribed: wsSubscribed,
    totalValue: wsTotalValue,
    totalPnL: wsTotalPnL,
    pnL24h: wsPnL24h,
    activeBots: wsActiveBots,
    positions: wsPositions,
    refreshPortfolio: wsRefreshPortfolio
  } = usePortfolioWebSocket({
    onPortfolioUpdate: (data) => {
      console.log('📊 Real-time portfolio update received:', data);
      // Update local state with WebSocket data
      if (data.positions) {
        setMyTokens(data.positions);
      }
    },
    onError: (error) => {
      console.error('Portfolio WebSocket error:', error);
    },
    autoSubscribe: true
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchMyTokens = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (activeWallet?.id) {
        params.append('walletId', activeWallet.id);
      }

      const data = await makeAuthenticatedRequest(`/portfolio/positions?${params}`);

      if (data.success) {
        setMyTokens(data.data.positions || []);
      } else {
        throw new Error(data.error || 'Failed to fetch positions');
      }
    } catch (error) {
      setError(error.message);
      console.error('Error fetching my tokens:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshPortfolio = async () => {
    try {
      setRefreshing(true);

      const requestBody = {};
      if (activeWallet?.id) {
        requestBody.walletId = activeWallet.id;
      }

      await makeAuthenticatedRequest('/portfolio/refresh', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      // Refetch positions after refresh
      await fetchMyTokens();
    } catch (error) {
      console.error('Error refreshing portfolio:', error);
      setError('Failed to refresh portfolio data');
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch tokens on component mount and when dependencies change
  useEffect(() => {
    if (tokens?.access_token) {
      fetchMyTokens();
    }
  }, [tokens, activeWallet, refreshTrigger]);

  const formatCurrency = (value) => {
    if (!value || value === 0) return '$0.00';
    return `$${value.toFixed(2)}`;
  };

  const formatPercentage = (percent) => {
    if (!percent || percent === 0) return '0.00%';
    const formatted = percent.toFixed(2);
    return `${percent >= 0 ? '+' : ''}${formatted}%`;
  };

  const getPerformanceColor = (value) => {
    if (!value || value === 0) return '';
    return value >= 0 ? 'positive' : 'negative';
  };

  const sortedTokens = [...myTokens].sort((a, b) => {
    // Sort by USD value descending
    const aValue = a.usdValue || a.currentValue || 0;
    const bValue = b.usdValue || b.currentValue || 0;
    return bValue - aValue;
  });

  if (!tokens?.access_token) {
    return (
      <div className="my-tokens-tab">
        <div className="auth-required">
          <div className="auth-icon">🔐</div>
          <h3>Authentication Required</h3>
          <p>Please log in to view your token positions</p>
        </div>
      </div>
    );
  }

  if (!activeWallet) {
    return (
      <div className="my-tokens-tab">
        <div className="wallet-required">
          <div className="wallet-icon">👛</div>
          <h3>Wallet Required</h3>
          <p>Please connect a wallet to view your token positions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-tokens-tab">
      <div className="portfolio-summary">
        <div className="summary-header">
          <h3>My Portfolio</h3>
          <div className="summary-actions">
            <button
              className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
              onClick={() => {
                if (wsConnected) {
                  wsRefreshPortfolio();
                } else {
                  refreshPortfolio();
                }
              }}
              disabled={refreshing}
              title={wsConnected ? "Refresh real-time portfolio data" : "Refresh portfolio data"}
            >
              {refreshing ? '🔄' : '↻'} {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>

            {wsConnected && (
              <div className="websocket-status">
                <span className="status-indicator connected">●</span>
                <span className="status-text">Live Updates</span>
              </div>
            )}
          </div>
        </div>

        <div className="summary-metrics">
          <div className="metric">
            <span className="label">Total Value</span>
            <span className="value">
              {formatCurrency(wsTotalValue || portfolioSummary.totalValue || 0)}
            </span>
            {wsConnected && <span className="live-indicator">🔴 Live</span>}
          </div>

          <div className="metric">
            <span className="label">Total P&L</span>
            <span className={`value ${getPerformanceColor(wsTotalPnL || portfolioSummary.percentagePnL)}`}>
              {formatPercentage(wsTotalPnL || portfolioSummary.percentagePnL || 0)}
            </span>
          </div>

          <div className="metric">
            <span className="label">24h P&L</span>
            <span className={`value ${getPerformanceColor(wsPnL24h)}`}>
              {formatPercentage(wsPnL24h || 0)}
            </span>
          </div>

          <div className="metric">
            <span className="label">Active Bots</span>
            <span className="value">{wsActiveBots || portfolioSummary.activeBots || 0}</span>
          </div>

          <div className="metric">
            <span className="label">Positions</span>
            <span className="value">{wsPositions?.length || myTokens.length}</span>
          </div>
        </div>

        {portfolioSummary.topPerformer && (
          <div className="performance-highlights">
            <div className="performer top-performer">
              <span className="label">🏆 Top Performer:</span>
              <span className="token">{portfolioSummary.topPerformer.symbol}</span>
              <span className="performance positive">
                {formatPercentage(portfolioSummary.topPerformer.pnl)}
              </span>
            </div>

            {portfolioSummary.worstPerformer && portfolioSummary.worstPerformer.symbol !== portfolioSummary.topPerformer.symbol && (
              <div className="performer worst-performer">
                <span className="label">📉 Needs Attention:</span>
                <span className="token">{portfolioSummary.worstPerformer.symbol}</span>
                <span className="performance negative">
                  {formatPercentage(portfolioSummary.worstPerformer.pnl)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          {error}
          <button className="error-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your token positions...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && sortedTokens.length === 0 && !error && (
        <div className="empty-state">
          <div className="empty-icon">💼</div>
          <h3>No tokens in portfolio</h3>
          <p>Start trading to build your portfolio. You can buy tokens from the Trending or Search tabs.</p>
        </div>
      )}

      {/* Token Positions Grid */}
      {!loading && sortedTokens.length > 0 && (
        <div className="tokens-grid">
          {sortedTokens.map(token => (
            <TokenPositionCard
              key={`${token.address}-${activeWallet.id}`}
              token={token}
              onQuickSell={() => onQuickSell(token)}
              onAddToBot={() => onAddToBot(token, token)} // Pass token as existing position
              onSetAlert={() => onSetAlert(token)}
            />
          ))}
        </div>
      )}

      {/* Portfolio Statistics */}
      {!loading && sortedTokens.length > 0 && (
        <div className="portfolio-stats">
          <h4>Portfolio Statistics</h4>
          <div className="stats-grid">
            <div className="stat">
              <span className="stat-label">Total Invested</span>
              <span className="stat-value">
                {formatCurrency(portfolioSummary.totalInvested || 0)}
              </span>
            </div>

            <div className="stat">
              <span className="stat-label">Realized P&L</span>
              <span className={`stat-value ${getPerformanceColor(portfolioSummary.realizedPnL)}`}>
                {formatCurrency(portfolioSummary.realizedPnL || 0)}
              </span>
            </div>

            <div className="stat">
              <span className="stat-label">Unrealized P&L</span>
              <span className={`stat-value ${getPerformanceColor(portfolioSummary.unrealizedPnL)}`}>
                {formatCurrency(portfolioSummary.unrealizedPnL || 0)}
              </span>
            </div>

            <div className="stat">
              <span className="stat-label">Avg Position Size</span>
              <span className="stat-value">
                {formatCurrency(sortedTokens.length > 0 ? (portfolioSummary.totalValue || 0) / sortedTokens.length : 0)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTokensTab;